import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { debounce } from '@/infra/util/debounce';
import { saveCurrentSnapshot } from './save';
import {
  applyDecay,
  applyRestore,
  isLow,
  VITALITY_MAX,
} from '@/domain/stats/vitality';
import {
  ALL_BIOMES,
  DAY_MS_FAST,
  DAY_MS_PROD,
  VITALITY_TELEPORT_PENALTY_HAPPINESS,
  WORLD_PIXEL_W,
  WORLD_PIXEL_H,
} from '@/domain/world/types';
import type { BiomeId, RegionItem } from '@/domain/world/types';
import { currentBiome, regionBounds } from '@/domain/world/region';
import { spawnRegionItems } from '@/domain/world/items';
import { exposureMultiplier } from '@/domain/world/exposure';
import type { NpcCreature } from '@/domain/npc/types';
import { hashCode } from '@/domain/world/rng';

const FAST = import.meta.env.VITE_FAST_LIFECYCLE === '1';
const DAY_MS = FAST ? DAY_MS_FAST : DAY_MS_PROD;

let lastTickAt = 0;
let lowWarned = false;

const autosave = debounce(() => {
  void saveCurrentSnapshot();
}, 1_500);

export interface WorldServiceApi {
  enterWorld: () => void;
  exitWorld: (penalize?: boolean) => void;
  tick: (nowMs?: number) => void;
  pickItemNear: (x: number, y: number, radius: number) => void;
  ensureItemsForToday: () => void;
  assignNpcHomeBiomes: () => void;
  isInWorld: () => boolean;
}

function ensureItemsForToday(): void {
  const s = useStore.getState();
  const created = s.world.dayCount;
  const expectedDay = currentDayCount();
  // Spawnar items se: dayCount mudou OU nenhum bioma tem items
  const empty = ALL_BIOMES.every((b) => s.world.regionItems[b].length === 0);
  if (created !== expectedDay || empty) {
    s.setDayCount(expectedDay);
    for (const b of ALL_BIOMES) {
      s.setRegionItems(b, spawnRegionItems(b, expectedDay));
    }
  }
}

function currentDayCount(): number {
  const c = useStore.getState().creature;
  if (!c) return 0;
  return Math.floor((Date.now() - c.lifecycle.bornAt) / DAY_MS);
}

function assignNpcHomeBiomes(): void {
  const s = useStore.getState();
  const npcs = s.npcs;
  let changed = false;
  const next: NpcCreature[] = npcs.map((n) => {
    if (n.homeBiome) return n;
    const idx = hashCode(n.id) % ALL_BIOMES.length;
    changed = true;
    return { ...n, homeBiome: ALL_BIOMES[idx]! };
  });
  if (changed) s.setNpcs(next);
}

function enterWorld(): void {
  const s = useStore.getState();
  if (!s.creature || s.creature.state.dead) return;
  ensureItemsForToday();
  assignNpcHomeBiomes();

  // Remove focus de qualquer botão ativo (evita Arrow keys ativarem o botão)
  if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // Player começa no centro do último bioma visitado (ou meadow)
  const lastB = s.world.lastBiome ?? 'meadow';
  const b = regionBounds(lastB);
  const px = b.x + Math.floor(b.w / 2);
  const py = b.y + Math.floor(b.h / 2);
  s.setPlayerPos(px, py);
  s.setInWorld(true);
  s.discoverRegion(lastB);
  lastTickAt = Date.now();
  lowWarned = false;
  eventBus.emit('world.entered', { biome: lastB });
  logger.info('world.entered', { biome: lastB });
}

function exitWorld(penalize = false): void {
  const s = useStore.getState();
  if (!s.inWorld) return;
  if (penalize && s.creature && !s.creature.state.dead) {
    const c = s.creature;
    const next = {
      ...c,
      state: {
        ...c.state,
        happiness: Math.max(0, c.state.happiness - VITALITY_TELEPORT_PENALTY_HAPPINESS),
      },
    };
    s.replaceCreature(next);
    eventBus.emit('world.teleported-home', { creatureId: c.id });
    logger.warn('world.teleported-home', { creatureId: c.id });
  }
  s.setInWorld(false);
  eventBus.emit('world.exited', {});
  autosave();
}

function tick(nowMs: number = Date.now()): void {
  const s = useStore.getState();
  const c = s.creature;
  if (!c || c.state.dead) return;
  const dt = nowMs - lastTickAt;
  lastTickAt = nowMs;
  if (dt <= 0) return;
  const dtSec = dt / 1000;

  if (s.inWorld) {
    const biome = currentBiome(s.playerX, s.playerY);
    const mult = exposureMultiplier(biome, c.genome.primaryTypes) * (FAST ? 5 : 1);
    const nextV = applyDecay(c.state.vitality, dtSec, mult);
    s.replaceCreature({ ...c, state: { ...c.state, vitality: nextV } });

    // Aviso uma vez quando entra em low
    if (isLow(nextV) && !lowWarned) {
      lowWarned = true;
      eventBus.emit('world.vitality-low', { vitality: nextV });
    } else if (!isLow(nextV)) {
      lowWarned = false;
    }

    // Teleport home se vitality = 0
    if (nextV <= 0) {
      exitWorld(true);
      return;
    }
    autosave();
  } else {
    // Em casa: restore
    if (c.state.vitality < VITALITY_MAX) {
      const nextV = applyRestore(c.state.vitality, dtSec);
      s.replaceCreature({ ...c, state: { ...c.state, vitality: nextV } });
    }
  }
}

function pickItemNear(x: number, y: number, radius: number): void {
  const s = useStore.getState();
  const biome = currentBiome(x, y);
  const items = s.world.regionItems[biome];
  for (const it of items) {
    if (it.collected) continue;
    const dx = x - it.x;
    const dy = y - it.y;
    if (dx * dx + dy * dy < radius * radius) {
      pickItem(biome, it);
      break;
    }
  }
}

function pickItem(biome: BiomeId, it: RegionItem): void {
  const s = useStore.getState();
  s.markItemCollected(biome, it.id);
  s.addToInventory(it.type, 1);
  eventBus.emit('world.item-collected', { type: it.type });
  autosave();
}

let api: WorldServiceApi | null = null;

export function getWorldService(): WorldServiceApi {
  if (api) return api;
  api = {
    enterWorld,
    exitWorld,
    tick,
    pickItemNear,
    ensureItemsForToday,
    assignNpcHomeBiomes,
    isInWorld: () => useStore.getState().inWorld,
  };
  return api;
}

export function resetWorldServiceForTests(): void {
  api = null;
  lastTickAt = 0;
  lowWarned = false;
}

export const WORLD_BOUNDS = { w: WORLD_PIXEL_W, h: WORLD_PIXEL_H };
